import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { AttachmentIcon, ChevronDownIcon, DeleteIcon } from '@treams/treams-icons';
import { TranscriptionStatus, TranscriptionUploadStatus } from '@treams/treams-network';
import {
	Button,
	FlexGap,
	FormattedTranscript,
	IconWrapper,
	Modal,
	Paragraph,
	Popover,
	type TranscriptionSpeakerInfo,
	tokens,
} from '@treams/treams-ui';

import { AudioPlayer } from './AudioPlayer';

const StyledButton = styled(Button)`
	font-weight: normal;
`;

const TranscriptText = styled(Box)`
	line-height: ${tokens.typography.lineHeights.body};
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.compact}px ${tokens.padding.container}px;
	background-color: ${tokens.brand.background};
	border: 1px solid ${tokens.brand.border};
	font-size: ${tokens.typography.sizes.small}px;
	max-height: 500px;
	overflow-y: auto;
`;

type TranscriptionData = {
	id: string;
	status: TranscriptionStatus;
	uploadStatus: TranscriptionUploadStatus;
	transcript?: string | null;
	audioUrl?: string | null;
	speakers?: TranscriptionSpeakerInfo[] | null;
	createdAt: string;
};

type Props = {
	transcription: TranscriptionData;
	onRemove?: () => void;
	removing?: boolean;
	canRemove?: boolean;
	label?: string;
	/** A streamed transcript is empty until the first turn arrives. */
	recording?: boolean;
};

export const TranscriptionAttachment = (props: Props) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const [confirmingRemove, setConfirmingRemove] = useState(false);

	const isLoading =
		!props.transcription ||
		props.transcription.uploadStatus === TranscriptionUploadStatus.UploadPending ||
		props.transcription.status === TranscriptionStatus.Pending ||
		props.transcription.status === TranscriptionStatus.Processing;

	const hasTranscript = !isLoading && !!props.transcription?.transcript;
	const hasAudio = !isLoading && !!props.transcription?.audioUrl;
	const isEmptyTranscription = !isLoading && !hasTranscript && !hasAudio && !props.recording;
	const label = isLoading
		? t('transcription.processing')
		: (props.label ?? t('transcription.meetingTranscription'));

	const removeAllowed = props.canRemove && props.onRemove;

	return (
		<FlexGap gap={8} flexDirection="column">
			{isEmptyTranscription ? (
				<FlexGap gap={12} flexDirection="column" alignItems="flex-start">
					<Paragraph small>{t('transcription.emptyTranscriptHint')}</Paragraph>
					{removeAllowed && (
						<Button
							type="ai"
							small
							title={t('transcription.emptyTranscriptRemoveAction')}
							onClick={() => {
								void props.onRemove?.();
							}}
							loading={props.removing}
						/>
					)}
				</FlexGap>
			) : (
				<FlexGap gap={8} alignItems="center" justifyContent="space-between">
					<StyledButton
						type="ai"
						title={
							<FlexGap gap={4} alignItems="center">
								<div>{label}</div>
								{hasTranscript && (
									<IconWrapper
										size={20}
										color="primary"
										mt={1}
										style={{
											transform: expanded ? 'rotate(180deg)' : 'none',
											transition: 'transform 0.2s ease',
										}}
									>
										<ChevronDownIcon />
									</IconWrapper>
								)}
							</FlexGap>
						}
						width="fit-content"
						icon={
							<IconWrapper size={isLoading ? 16 : 20} style={{ scale: isLoading ? 1 : 0.8 }}>
								<AttachmentIcon />
							</IconWrapper>
						}
						disabled={isLoading}
						onClick={hasTranscript ? () => setExpanded((prev) => !prev) : undefined}
					/>
					{removeAllowed && (
						<Button
							type="plain"
							icon={
								<IconWrapper size={20}>
									<DeleteIcon />
								</IconWrapper>
							}
							onClick={() => setConfirmingRemove(true)}
							disabled={props.removing}
							small
						/>
					)}
				</FlexGap>
			)}
			{removeAllowed && !isEmptyTranscription && (
				<Popover center visible={confirmingRemove}>
					<Modal>
						<Modal.Header
							title={t('common.areYouSure')}
							onClose={() => setConfirmingRemove(false)}
						/>
						<Modal.Contents>
							<Paragraph>{t('transcription.confirmRemove')}</Paragraph>
						</Modal.Contents>
						<Modal.FormFooter
							onCancel={() => setConfirmingRemove(false)}
							onSave={props.onRemove}
							saveTitle={t('common.confirm')}
							disabled={props.removing}
						/>
					</Modal>
				</Popover>
			)}
			<AnimatePresence>
				{expanded && hasTranscript && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
						style={{ overflow: 'hidden' }}
					>
						<FlexGap gap={8} flexDirection="column">
							{hasAudio && <AudioPlayer src={props.transcription!.audioUrl!} />}
							<TranscriptText mb="8px">
								<FormattedTranscript
									text={props.transcription!.transcript!}
									speakers={props.transcription!.speakers}
								/>
							</TranscriptText>
						</FlexGap>
					</motion.div>
				)}
			</AnimatePresence>
		</FlexGap>
	);
};
